import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    property: { type: String, default: "--scroll-progress" },
  }

  connect() {
    // When native scroll-driven animations are available the rotation runs on
    // the compositor via CSS (see the `scroll-rotation` utility), so there is
    // no need to track scroll progress on the main thread at all.
    this.native = CSS.supports("animation-timeline: scroll()")
    if (this.native) return

    this.rafId = null
    this.idleFrames = 0
    this.lastProgress = null
    this.tick = this.tick.bind(this)

    this.cacheMaxScroll()
    this.onResize = () => this.cacheMaxScroll()
    window.addEventListener("resize", this.onResize, { passive: true })

    this.render()
  }

  disconnect() {
    if (this.onResize) window.removeEventListener("resize", this.onResize)
    if (this.rafId) cancelAnimationFrame(this.rafId)
  }

  cacheMaxScroll() {
    // Reading scrollHeight forces layout, so do it once up front (and on
    // resize) instead of on every frame.
    this.maxScroll = document.documentElement.scrollHeight - window.innerHeight
  }

  onScroll() {
    if (this.native) return

    // iOS Safari throttles `scroll` events during momentum scrolling, so
    // reacting only to those events makes the rotation update in coarse steps
    // and lag behind the finger. Instead we use the scroll event purely as a
    // trigger to start a rAF loop that samples window.scrollY every frame
    // (the compositor keeps it current between scroll events) and stops itself
    // once scrolling settles.
    if (!this.rafId) {
      this.idleFrames = 0
      this.rafId = requestAnimationFrame(this.tick)
    }
  }

  tick() {
    const changed = this.render()
    this.idleFrames = changed ? 0 : this.idleFrames + 1

    // Keep polling while the position is still moving, and for a short grace
    // period afterwards to bridge gaps in momentum, then stop so we don't burn
    // frames while the page is at rest.
    if (this.idleFrames < 10) {
      this.rafId = requestAnimationFrame(this.tick)
    } else {
      this.rafId = null
    }
  }

  render() {
    const progress =
      this.maxScroll > 0
        ? Math.max(0, Math.min(window.scrollY / this.maxScroll, 1))
        : 0

    const value = progress.toFixed(4)
    if (value === this.lastProgress) return false

    this.lastProgress = value
    this.element.style.setProperty(this.propertyValue, value)
    return true
  }
}
